import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, type Selection } from '@atlaskit/editor-prosemirror/state';
import { getNativeEmbedCommentSupportStore } from '@atlaskit/native-embeds-common/comment-support-store';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

const commentsEnabled = (node: ProseMirrorNode): boolean =>
	getNativeEmbedCommentSupportStore().getSnapshot()(node);

export type ExtensionCommentsEligibility = {
	node?: ProseMirrorNode | null;
	isCommentsEnabled?: (node: ProseMirrorNode) => boolean;
};

export type CommentToolbarIconEligibility = {
	canAddComments: boolean;
	isGeneratingOrReplacing: boolean;
	isOffline: boolean;
	isReadOnly: boolean;
	selection: Selection;
};

/** Native embeds owns capability; Confluence owns the rollout policy. */
export const isExtensionCommentsExperienceEnabled = ({
	node,
	isCommentsEnabled = commentsEnabled,
}: ExtensionCommentsEligibility): boolean => {
	if (node?.type.name !== 'extension') {
		return false;
	}
	try {
		return isCommentsEnabled(node) && expVal('cc_maui_comments_on_charts_exp', 'isEnabled', false);
	} catch {
		return false;
	}
};

export const canShowCommentToolbarIcon = ({
	canAddComments,
	isGeneratingOrReplacing,
	isOffline,
	isReadOnly,
	selection,
}: CommentToolbarIconEligibility): boolean => {
	if (
		!canAddComments ||
		isGeneratingOrReplacing ||
		isOffline ||
		isReadOnly ||
		!(selection instanceof NodeSelection)
	) {
		return false;
	}
	return isExtensionCommentsExperienceEnabled({ node: selection.node });
};

/** Renderer viewing support is independent of the new-thread creation experiment. */
export const isRendererExtensionCommentBlockNodeSupported = (
	node: ProseMirrorNode,
	isCommentsEnabled: (node: ProseMirrorNode) => boolean = commentsEnabled,
): boolean => {
	try {
		return isCommentsEnabled(node);
	} catch {
		return false;
	}
};

// Providers supply the current capability snapshot from the shared native-embed registry.
export const isExtensionCommentBlockNodeSupported = (
	node: ProseMirrorNode,
	isCommentsEnabled: (node: ProseMirrorNode) => boolean = commentsEnabled,
): boolean => isExtensionCommentsExperienceEnabled({ node, isCommentsEnabled });

export const isEditorExtensionCommentBlockNodeSupported = (node: ProseMirrorNode): boolean => {
	if (
		node.type.name === 'extension' &&
		node.attrs.extensionKey === 'native-embed:maui' &&
		fg('cc_maui_annotations_on_extensions')
	) {
		// Keep saved chart comments accessible when the toolbar's creation experiment is off.
		return isRendererExtensionCommentBlockNodeSupported(node);
	}

	return isExtensionCommentBlockNodeSupported(node);
};
